import { animate, AUTO_STYLE, state, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

// Services
import { CmxLayoutService } from '../../../services/cmx-layout.service';
import { MenuService } from '../../../services/menu.service';

// Types
import { ApplicationMenu, Menu } from '../../../types/menu';

@Component({
  selector: 'cmx-nav-menu',
  templateUrl: './cmx-nav-menu.component.html',
  styleUrls: ['./cmx-nav-menu.component.scss'],
  animations: [
    trigger('dropdown', [
      state('void', style({ height: '0' })),
      state('*', style({ height: AUTO_STYLE })),
      transition('void => *', animate('.2s ease-in-out')),
      transition('* => void', animate('.2s ease-in')),
    ]),
    trigger('slideout', [
      state('void', style({ width: '0' })),
      state('*', style({ width: AUTO_STYLE })),
      transition('void => *', animate('.2s ease-in-out')),
      transition('* => void', animate('.2s ease-in')),
    ]),
  ],
})
export class CmxNavMenuComponent implements OnInit, OnDestroy {
  public isMobile: boolean;
  public isMobileMenuOpen: boolean;
  public isCollapsed: boolean;
  public isLegalEntityListOpen: boolean;
  public isRtl: boolean;
  public version: number;
  public menuItemIdActive: number;
  public subMenuItemIdActive: number;
  public menuItemsData: ApplicationMenu[] = [];

  public menuItemSelectedId: number;
  @ViewChild('menuRef', { static: true })
  menuRef: ElementRef;
  public variant: string;

  private subscriptions: Subscription[] = [];

  constructor(
    private layout: CmxLayoutService,
    public menu: MenuService,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.layout.isMobile.subscribe((value) => {
        this.isMobile = value;
      }),
      this.layout.isMobileMenuOpen.subscribe((value) => (this.isMobileMenuOpen = value)),
      this.layout.direction.subscribe((value) => (value === 'rtl' ? (this.isRtl = true) : (this.isRtl = false))),
      this.layout.isCollapsed.subscribe((value) => {
        this.isCollapsed = value;
        if (this.isCollapsed) {
          this.menuItemsData.forEach((menuItem) => {
            this.identifyCurrentPathToActiveItemMenu(menuItem);
          });
        }
      }),
      this.layout.isLegalEntityListOpen.subscribe((value) => (this.isLegalEntityListOpen = value)),
      this.menu.menuItemSelectedId.subscribe((value) => (this.menuItemSelectedId = value)),
      this.layout.variantHeader.subscribe((value) => (this.variant = value)),
      this.layout.version.subscribe((value) => (this.version = value)),
    );
    this.menu.menuItems.subscribe((data) => {
      this.menuItemsData = data;
      if (!!this.menuItemsData) {
        this.menuItemsData.forEach((menuItem) => {
          this.identifyCurrentPathToActiveItemMenu(menuItem);
        });
      }
    });
  }

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (this.menuRef && !this.menuRef.nativeElement.contains(event.target) && this.isCollapsed) {
      this.menu.menuItemSelectedId.next(-1);
    }
  }

  menuItemClick(menuItem: Menu) {
    if (menuItem.menuItems.length === 0) {
      this.menu.menuItemSelectedId.next(-1);
      this.menu.menuClick.emit(menuItem.menuId);
      this.layout.isMobileMenuOpen.next(false);
    } else {
      if (this.menuItemSelectedId === menuItem.menuId) {
        this.menu.menuItemSelectedId.next(-1);
      } else {
        this.menu.menuItemSelectedId.next(menuItem.menuId);
      }
    }
  }

  identifyCurrentPathToActiveItemMenu(menuItem: ApplicationMenu) {
    const pathname: string = window.location.pathname;
    const isCurrentPath = pathname.includes(menuItem.menu.menuEndpoint);
    if (isCurrentPath) {
      this.menuItemIdActive = menuItem.menu.menuId;
    }
    if (menuItem.menu.menuItems.length) {
      menuItem.menu.menuItems.forEach((subMenuItem) => {
        const isCurrentSubPath = pathname.includes(subMenuItem.menuEndpoint);
        if (isCurrentSubPath) {
          this.subMenuItemIdActive = subMenuItem.menuId;
        }
      });
    }
  }

  subItemClick(menuItem: Menu) {
    this.menu.menuClick.emit(menuItem.menuId);
    this.layout.isMobileMenuOpen.next(false);
  }

  /**
   * @description - This method is used to activate the menu item when the user is on the same page
   * @param menuToTest - The menu item to test
   * @param displayFlag - The flag to activate the menu item
   * @returns - The flag to activate the menu item
   */
  public shouldDisplayActiveMenu(menu: Menu, displayFlag: boolean): boolean {
    const pathname: string = window.location.pathname;
    let isItMyChild = false;
    if (menu) {
      // if it's a application menu, check if it's my child
      if (menu.menuItems) {
        isItMyChild =
          menu.menuItems.findIndex((menuItem) => {
            // compare the menu endpoint with the pathname
            return menuItem.menuEndpoint.indexOf(pathname) !== -1;
          }) !== -1
            ? true
            : false;
      } else {
        // if it's a menu item, check if it's my child
        isItMyChild = menu.menuEndpoint.indexOf(pathname) !== -1 ? true : false;
      }
    }
    return isItMyChild || displayFlag;
  }

  ngOnDestroy() {
    this.subscriptions.map((sub) => sub.unsubscribe());
  }
}
